import React from "react";
import CcButton from "../../CoilConfigurator/components/CcButton";
import WarningBanner from "../components/WarningBanner";
import PipeGeometryTiles from "../components/PipeGeometryTiles";
import StepDownThresholdGauge from "../components/StepDownThresholdGauge";
import type {
  ConfigurationResult,
  EquipmentConfiguration,
  EquipmentKind,
  EquipmentOption,
  MatchedCatalogue,
} from "../models/types";

interface ConfigurationResultViewProps {
  result: ConfigurationResult;
  onBackToList: () => void;
  onViewEquipment: (kind: EquipmentKind) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onRecalculate?: () => void;
  recalculating?: boolean;
  deleting?: boolean;
}

const dash = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : "—";
};

const optionByExactName = (equipment: EquipmentConfiguration, name: string) =>
  equipment.options.find((option) => option.name === name) ?? null;

const calculationByExactLabel = (equipment: EquipmentConfiguration, label: string) =>
  equipment.calculations.find((item) => item.label === label)?.value ?? null;

const mapCwcOptionStatus = (status: EquipmentOption["status"] | undefined) => {
  if (status === "Selected") return "YES";
  if (status === "Not Selected") return "NO";
  if (status === "Review Required") return "Review Required";
  return "—";
};

const mapStepDownOptionStatus = (status: EquipmentOption["status"] | undefined) => {
  if (status === "Selected") return "Yes";
  if (status === "Not Selected") return "No";
  if (status === "Review Required") return "Review Required";
  return "—";
};

const blastHeadSize = (
  equipment: EquipmentConfiguration,
  matchedCatalogue?: MatchedCatalogue | null,
) => {
  const selectedHead = equipment.features.find(
    (feature) =>
      (feature.name === "Large Heads" || feature.name === "Small Heads") && feature.selected,
  );
  if (selectedHead) return selectedHead.name;

  const namedHead = equipment.features.find(
    (feature) => feature.name === "Large Heads" || feature.name === "Small Heads",
  );
  if (namedHead) return namedHead.name;

  return dash(matchedCatalogue?.blastFrame?.selected?.heads ?? undefined);
};

const blastCarriageKiteType = (
  equipment: EquipmentConfiguration,
  matchedCatalogue?: MatchedCatalogue | null,
) => {
  const kiteId = matchedCatalogue?.blastFrame?.selected?.kite?.id?.trim();
  if (kiteId) return kiteId;

  const selectedOption = equipment.options.find(
    (option) => option.name !== "CWC config" && option.status === "Selected",
  );
  if (selectedOption?.name?.trim()) return selectedOption.name.trim();

  const reviewRequired = equipment.options.some(
    (option) => option.name !== "CWC config" && option.status === "Review Required",
  );
  if (reviewRequired) return "Review Required";

  return "—";
};

const blastFrameRows = (
  equipment: EquipmentConfiguration,
  matchedCatalogue?: MatchedCatalogue | null,
): [string, string][] => {
  const cwcOption = optionByExactName(equipment, "CWC config");

  return [
    ["Frame Size", dash(equipment.diameterCategory)],
    ["Version", dash(equipment.lengthCategory)],
    ["Blast Head Size", blastHeadSize(equipment, matchedCatalogue)],
    ["Carriage / Kite Type", blastCarriageKiteType(equipment, matchedCatalogue)],
    ["CWC Config?", mapCwcOptionStatus(cwcOption?.status)],
    ["Rule Version", dash(equipment.ruleVersion)],
  ];
};

const inductionCoilCwc = (
  equipment: EquipmentConfiguration,
  matchedCatalogue?: MatchedCatalogue | null,
) => {
  const fromCalc = calculationByExactLabel(equipment, "CWC hardware");
  if (fromCalc) return dash(fromCalc);

  const fromSelected = matchedCatalogue?.inductionCoil?.selected?.cwcHardware;
  if (fromSelected) return dash(fromSelected);

  return mapCwcOptionStatus(optionByExactName(equipment, "CWC config")?.status);
};

const inductionCoilRows = (
  equipment: EquipmentConfiguration,
  matchedCatalogue?: MatchedCatalogue | null,
): [string, string][] => {
  const stepDownOption = optionByExactName(equipment, "Step down");

  return [
    ["Frame Size", dash(equipment.diameterCategory)],
    ["Bottom End Version", dash(equipment.lengthCategory)],
    ["CWC Config?", inductionCoilCwc(equipment, matchedCatalogue)],
    ["Step-Down Config?", mapStepDownOptionStatus(stepDownOption?.status)],
    ["Rule Version", dash(equipment.ruleVersion)],
  ];
};

const SpecRow = ({ label, value }: { label: string; value: string }) => (
  <div className="op-spec-row">
    <span className="op-spec-row__k">{label}</span>
    <span className={`op-spec-row__v${value === "—" ? " op-spec-row__v--dash" : ""}`}>{value}</span>
  </div>
);

const EquipmentSpecCard = ({
  title,
  partNo,
  status,
  rows,
  onViewDetails,
}: {
  title: string;
  partNo: string;
  status?: string;
  rows: [string, string][];
  onViewDetails: () => void;
}) => (
  <article className="op-spec-card">
    <header className="op-spec-card__head">
      <div>
        <h3>{title}</h3>
        {status ? <p className="op-spec-card__status">{status}</p> : null}
      </div>
      <span className="op-spec-card__part">{partNo}</span>
    </header>
    <div className="op-spec-card__body">
      {rows.map(([label, value]) => (
        <SpecRow key={label} label={label} value={value} />
      ))}
    </div>
    <div className="op-spec-card__actions">
      <CcButton variant="secondary" onClick={onViewDetails}>
        View Details
      </CcButton>
    </div>
  </article>
);

const ConfigurationResultView = ({
  result,
  onBackToList,
  onViewEquipment,
  onEdit,
  onDelete,
  onRecalculate,
  recalculating = false,
  deleting = false,
}: ConfigurationResultViewProps) => {
  const steelOd = result.pipe.outsideDiameter
    ? `${result.pipe.outsideDiameter} ${result.pipe.outsideDiameterUnit || "mm"}`
    : "—";
  const coating = result.process.coatingThickness
    ? `${result.process.coatingThickness} ${result.process.coatingThicknessUnit || "mm"}`
    : "—";
  const actionsBusy = recalculating || deleting;

  return (
    <div className="op-result">
      <CcButton variant="link" className="op-back-link" onClick={onBackToList}>
        ← All Configurations
      </CcButton>

      <div className="op-detail-head">
        <div className="op-detail-head__left">
          <div>
            <h2 className="op-section-title">
              {result.project.projectNumber} · {result.project.projectName || result.configurationId}
            </h2>
            <p className="op-muted">
              {result.project.customer || "—"} · {steelOd} steel pipe
              {coating !== "—" ? `, ${coating} coating` : ""}
            </p>
            <p className="op-muted">
              {result.configurationId} · Revision {result.revision}
              {result.versions
                ? ` · Dataset ${result.versions.datasetRevision} · BF ${result.versions.blastFrameRuleVersion} · IC ${result.versions.inductionCoilRuleVersion}`
                : ""}
            </p>
          </div>
          {onEdit || onDelete || onRecalculate ? (
            <div className="op-detail-head__actions">
              {onEdit ? (
                <CcButton variant="secondary" onClick={onEdit} disabled={actionsBusy}>
                  Edit
                </CcButton>
              ) : null}
              {onDelete ? (
                <CcButton variant="danger" onClick={onDelete} disabled={actionsBusy}>
                  {deleting ? "Deleting…" : "Delete"}
                </CcButton>
              ) : null}
              {onRecalculate ? (
                <CcButton variant="primary" onClick={onRecalculate} disabled={actionsBusy}>
                  {recalculating ? "Recalculating…" : "Recalculate"}
                </CcButton>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {(result.warnings.length > 0 || result.errorMessage) && (
        <div className="op-advisory-grid">
          <div className="op-advisory-card op-advisory-card--causes">
            <h3>Advisory Causes</h3>
            {result.warnings.length === 0 && !result.errorMessage ? (
              <p className="op-muted">No advisory conditions triggered.</p>
            ) : (
              <>
                {result.errorMessage ? <p>{result.errorMessage}</p> : null}
                {result.warnings.map((warning) => (
                  <WarningBanner key={`${warning.title}-${warning.message}`} warning={warning} />
                ))}
              </>
            )}
          </div>
          <div className="op-advisory-card op-advisory-card--actions">
            <h3>Advisory Actions</h3>
            <p className="op-muted">
              Review equipment details below, or adjust pipe/process inputs and regenerate if the fit
              is not acceptable.
            </p>
          </div>
        </div>
      )}

      <p className="op-section-label">Selected Equipment</p>
      {result.errorMessage && !result.blastFrame && !result.inductionCoil ? (
        <p className="op-field-error" role="alert">
          {result.errorMessage}
        </p>
      ) : (
        <div className="op-equip-grid op-equip-grid--detail">
          {result.blastFrame ? (
            <EquipmentSpecCard
              title="Manual Blast Frame"
              partNo={result.blastFrame.configurationId}
              status={result.blastFrame.status}
              rows={blastFrameRows(result.blastFrame, result.matchedCatalogue)}
              onViewDetails={() => onViewEquipment("blast-frame")}
            />
          ) : null}
          {result.inductionCoil ? (
            <EquipmentSpecCard
              title="Induction Heat Coil"
              partNo={result.inductionCoil.configurationId}
              status={result.inductionCoil.status}
              rows={inductionCoilRows(result.inductionCoil, result.matchedCatalogue)}
              onViewDetails={() => onViewEquipment("induction-coil")}
            />
          ) : null}
        </div>
      )}

      <PipeGeometryTiles
        pipe={result.pipe}
        process={result.process}
        inputSummary={result.inputSummary}
      />

      <StepDownThresholdGauge
        pipe={result.pipe}
        process={result.process}
        inputSummary={result.inputSummary}
      />
    </div>
  );
};

export default ConfigurationResultView;
